/**
 * MST layout: teacher sets QUESTIONS PER MODULE (not test total).
 * Each module: at least 12 items with ≥4 easy, ≥4 medium, ≥4 hard when count ≥ 12.
 * Module count follows passage length so each section has enough content for that quota.
 */

export const MST_MIN_MODULES = 2;
export const MST_MAX_MODULES = 10;

/** Per module: minimum to allow 4 + 4 + 4 difficulty bands. */
export const MST_MIN_QUESTIONS_PER_MODULE = 12;

/** Per module: UI / generation ceiling (was global test max before per-module semantics). */
export const MST_MAX_QUESTIONS_PER_MODULE = 60;

/** @deprecated use MST_MIN_QUESTIONS_PER_MODULE — kept for Elements imports */
export const MST_MIN_TARGET_QUESTION_COUNT = MST_MIN_QUESTIONS_PER_MODULE;

/** Below this per-module count we cannot enforce 4+4+4 within the module. */
export const MST_MIN_TOTAL_FOR_TRIPLE_FOUR = 12;

/** Default questions per module when not stored. */
export const MST_DEFAULT_TARGET_QUESTION_COUNT = 15;

/** Layout schema: 3 = per-module targets + content-based module count */
export const MST_LAYOUT_VERSION = 3;

/**
 * Clamp teacher "target" = questions to generate in EACH module.
 */
export function clampTargetQuestionCount(raw) {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return MST_DEFAULT_TARGET_QUESTION_COUNT;
  return Math.max(
    MST_MIN_QUESTIONS_PER_MODULE,
    Math.min(MST_MAX_QUESTIONS_PER_MODULE, n)
  );
}

/**
 * Minimum words per module so a slice can plausibly support `questionsPerModule` items.
 */
export function minWordsPerModuleForGeneration(questionsPerModule) {
  const q = clampTargetQuestionCount(questionsPerModule);
  return Math.max(120, Math.round(q * 22));
}

/**
 * Global easy/medium/hard counts: each ≥ 4 when total ≥ 12; sums to total.
 */
export function allocateGlobalDifficultyTotals(totalQuestions) {
  const T = Math.max(0, Math.floor(Number(totalQuestions) || 0));
  if (T < MST_MIN_TOTAL_FOR_TRIPLE_FOUR) {
    return balanceDifficultyThreeWay(T);
  }
  let rem = T - 12;
  const out = { easy: 4, medium: 4, hard: 4 };
  const order = ['easy', 'medium', 'hard'];
  let k = 0;
  while (rem > 0) {
    out[order[k % 3]] += 1;
    rem -= 1;
    k += 1;
  }
  return out;
}

function balanceDifficultyThreeWay(T) {
  const n = Math.max(0, Math.floor(T));
  if (n === 0) return { easy: 0, medium: 0, hard: 0 };
  const base = Math.floor(n / 3);
  const r = n - base * 3;
  const out = { easy: base, medium: base, hard: base };
  const order = ['easy', 'medium', 'hard'];
  for (let i = 0; i < r; i++) out[order[i]] += 1;
  return out;
}

/**
 * How many modules to use: enough words per module for `questionsPerModule` items
 * (12+ with 4/4/4 each), capped at MST_MAX_MODULES — avoids 10 tiny modules × 2 questions.
 */
export function suggestedModuleCount(wordCount, questionsPerModule) {
  const wc = Math.max(0, Number(wordCount) || 0);
  const qpm = clampTargetQuestionCount(questionsPerModule);
  if (wc < 1) return MST_MIN_MODULES;

  const minW = minWordsPerModuleForGeneration(qpm);
  const raw = Math.floor(wc / minW);
  const fromContent = Math.max(MST_MIN_MODULES, raw);
  return Math.min(MST_MAX_MODULES, fromContent);
}

export function renumberSectors(sectors) {
  return [...sectors]
    .sort((a, b) => (a.sectorStart || 0) - (b.sectorStart || 0))
    .map((s, i) => ({
      ...s,
      sectorNumber: i + 1,
    }));
}

function defaultTwoSectorSplit(wordCount) {
  const wc = Math.max(2, Number(wordCount) || 2);
  const mid = Math.max(1, Math.floor(wc / 2));
  return [
    { sectorNumber: 1, sectorName: 'Part 1', sectorStart: 1, sectorEnd: mid },
    { sectorNumber: 2, sectorName: 'Part 2', sectorStart: mid + 1, sectorEnd: wc },
  ];
}

function mergeSectorsToCount(sectors, targetM) {
  let s = renumberSectors(sectors);
  while (s.length > targetM) {
    let bestI = 0;
    let bestSpan = Infinity;
    for (let i = 0; i < s.length - 1; i++) {
      const span = (s[i + 1].sectorEnd || 0) - (s[i].sectorStart || 0) + 1;
      if (span < bestSpan) {
        bestSpan = span;
        bestI = i;
      }
    }
    const a = s[bestI];
    const b = s[bestI + 1];
    const merged = {
      sectorNumber: 0,
      sectorName: `${a.sectorName} · ${b.sectorName}`,
      sectorStart: a.sectorStart,
      sectorEnd: b.sectorEnd,
    };
    s.splice(bestI, 2, merged);
  }
  return renumberSectors(s);
}

function splitSectorsToCount(sectors, targetM) {
  let s = renumberSectors(sectors);
  while (s.length < targetM) {
    let bi = 0;
    let bestSpan = -1;
    for (let i = 0; i < s.length; i++) {
      const span = (s[i].sectorEnd || 0) - (s[i].sectorStart || 0) + 1;
      if (span > bestSpan) {
        bestSpan = span;
        bi = i;
      }
    }
    const seg = s[bi];
    const start = seg.sectorStart || 1;
    const end = seg.sectorEnd || start;
    const mid = Math.floor((start + end) / 2);
    if (mid <= start || mid >= end) break;
    const left = {
      ...seg,
      sectorEnd: mid,
      sectorName: `${seg.sectorName || 'Section'} (a)`,
    };
    const right = {
      ...seg,
      sectorStart: mid + 1,
      sectorName: `${seg.sectorName || 'Section'} (b)`,
    };
    s.splice(bi, 1, left, right);
  }
  return renumberSectors(s);
}

/**
 * Clamp sector list to [2, 10] by merging or splitting on word indices.
 */
export function normalizeSectorsForMst(sectors, wordCount, targetModuleCount) {
  const t = Math.max(MST_MIN_MODULES, Math.min(MST_MAX_MODULES, Math.floor(Number(targetModuleCount)) || 2));
  const wc = Math.max(1, Number(wordCount) || 1);

  if (!Array.isArray(sectors) || sectors.length === 0) {
    return defaultTwoSectorSplit(wc);
  }

  let s = renumberSectors(sectors);
  if (s.length > t) s = mergeSectorsToCount(s, t);
  if (s.length < t) s = splitSectorsToCount(s, t);

  s = renumberSectors(s);
  s.forEach((sec, idx) => {
    const start = Math.max(1, Math.min(wc, sec.sectorStart || 1));
    const end = Math.max(start, Math.min(wc, sec.sectorEnd || wc));
    sec.sectorStart = start;
    sec.sectorEnd = end;
    if (!sec.sectorName) sec.sectorName = `Module ${idx + 1}`;
  });
  return s;
}

/**
 * Per-module question counts summing to totalQuestions.
 * Last module is strictly greater than every earlier module when M ≥ 2.
 */
export function allocateModuleQuestionTargets(moduleCount, totalQuestions) {
  const T = Math.max(
    1,
    Math.floor(Number(totalQuestions) || MST_DEFAULT_TARGET_QUESTION_COUNT)
  );
  const M = Math.max(MST_MIN_MODULES, Math.min(MST_MAX_MODULES, Math.floor(Number(moduleCount)) || 2));

  const base = Math.floor(T / M);
  const r = T - base * M;
  const counts = Array.from({ length: M }, (_, i) => base + (i >= M - r ? 1 : 0));

  if (M >= 2) {
    let maxPrev = Math.max(...counts.slice(0, -1));
    while (counts[M - 1] <= maxPrev) {
      counts[M - 1] += 1;
      let j = M - 2;
      while (j >= 0 && counts[j] <= 1) j -= 1;
      if (j < 0) break;
      counts[j] -= 1;
      maxPrev = Math.max(...counts.slice(0, -1));
    }
  }

  const sum = counts.reduce((a, b) => a + b, 0);
  if (sum !== T) {
    counts[M - 1] += T - sum;
  }
  return counts;
}

/**
 * Per-module easy/medium/hard; rows sum to moduleQuestionCounts[i];
 * columns sum to globalDifficulty.
 */
export function allocateDifficultyQuotasPerModule(moduleQuestionCounts, globalDifficulty) {
  const M = moduleQuestionCounts.length;
  const G = {
    easy: Math.max(0, globalDifficulty.easy || 0),
    medium: Math.max(0, globalDifficulty.medium || 0),
    hard: Math.max(0, globalDifficulty.hard || 0),
  };
  const Gsum = G.easy + G.medium + G.hard;
  const total = moduleQuestionCounts.reduce((a, b) => a + b, 0);
  if (Gsum !== total || M === 0) {
    return moduleQuestionCounts.map((n) => balanceDifficultyThreeWay(n));
  }

  const rem = { easy: G.easy, medium: G.medium, hard: G.hard };
  const q = moduleQuestionCounts.map(() => ({ easy: 0, medium: 0, hard: 0 }));

  for (let iter = 0; iter < total; iter++) {
    let bi = -1;
    let bestNeed = -1;
    for (let i = 0; i < M; i++) {
      const used = q[i].easy + q[i].medium + q[i].hard;
      const need = moduleQuestionCounts[i] - used;
      if (need > bestNeed) {
        bestNeed = need;
        bi = i;
      }
    }
    if (bi < 0 || bestNeed <= 0) break;

    const ideal = {
      easy: (G.easy * moduleQuestionCounts[bi]) / Gsum,
      medium: (G.medium * moduleQuestionCounts[bi]) / Gsum,
      hard: (G.hard * moduleQuestionCounts[bi]) / Gsum,
    };

    const diffKeys = ['easy', 'medium', 'hard'].filter((d) => rem[d] > 0);
    if (diffKeys.length === 0) break;

    const pick = diffKeys.reduce((best, d) => {
      const score = ideal[d] - q[bi][d];
      const bestScore = ideal[best] - q[bi][best];
      return score > bestScore ? d : best;
    }, diffKeys[0]);

    q[bi][pick] += 1;
    rem[pick] -= 1;
  }

  return q;
}

/**
 * @param questionsPerModule — teacher target: same count in every module (each with 4/4/4 when ≥12).
 */
export function buildMstLayoutFromSectors(sectorsAfterNormalize, wordCount, questionsPerModule) {
  const qpm = clampTargetQuestionCount(
    questionsPerModule != null ? questionsPerModule : MST_DEFAULT_TARGET_QUESTION_COUNT
  );
  const M = sectorsAfterNormalize.length;
  const perModuleDifficulty = allocateGlobalDifficultyTotals(qpm);
  const perModuleQuestionTargets = Array.from({ length: M }, () => qpm);
  const perModuleDifficultyQuotas = Array.from({ length: M }, () => ({ ...perModuleDifficulty }));
  const totalQuestions = M * qpm;
  const globalDifficulty = {
    easy: perModuleDifficulty.easy * M,
    medium: perModuleDifficulty.medium * M,
    hard: perModuleDifficulty.hard * M,
  };

  return {
    version: MST_LAYOUT_VERSION,
    questionsPerModule: qpm,
    totalQuestions,
    globalDifficulty,
    moduleCount: M,
    wordCount: Number(wordCount) || 0,
    perModuleQuestionTargets,
    perModuleDifficultyQuotas,
    minQuestionsPerModule: MST_MIN_QUESTIONS_PER_MODULE,
    maxQuestionsPerModule: MST_MAX_QUESTIONS_PER_MODULE,
    minTotalForTripleFour: MST_MIN_TOTAL_FOR_TRIPLE_FOUR,
  };
}

/**
 * When some sectors return no questions, renumber to consecutive modules 1..K (reading order)
 * and align sectors + mstLayout with items actually generated.
 */
export function compactAmcqModulesAfterGeneration(questions, sectors, layout, wordCount) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return { questions, sectors: sectors || [], mstLayout: layout };
  }
  const oldOrder = [
    ...new Set(questions.map((q) => Math.max(1, Math.round(Number(q.sectorNumber)) || 1))),
  ].sort((a, b) => a - b);
  const remap = Object.fromEntries(oldOrder.map((old, i) => [old, i + 1]));

  const newQuestions = questions.map((q) => ({
    ...q,
    sectorNumber: remap[Math.max(1, Math.round(Number(q.sectorNumber)) || 1)] ?? 1,
  }));

  const sectorByOld = new Map((sectors || []).map((s) => [s.sectorNumber, s]));
  const newSectors = oldOrder.map((old, idx) => {
    const s = sectorByOld.get(old) || {};
    return {
      ...s,
      sectorNumber: idx + 1,
      sectorName: s.sectorName || `Module ${idx + 1}`,
      sectorStart: s.sectorStart,
      sectorEnd: s.sectorEnd,
    };
  });

  const perModuleQuestionTargets = newSectors.map(
    (s) => newQuestions.filter((q) => q.sectorNumber === s.sectorNumber).length
  );
  const perModuleDifficultyQuotas = newSectors.map((s) => {
    const o = { easy: 0, medium: 0, hard: 0 };
    newQuestions
      .filter((q) => q.sectorNumber === s.sectorNumber)
      .forEach((q) => {
        const d = String(q.difficultyLevel || '').toLowerCase();
        if (d === 'easy') o.easy += 1;
        else if (d === 'medium') o.medium += 1;
        else if (d === 'hard') o.hard += 1;
      });
    return o;
  });
  const globalDifficulty = perModuleDifficultyQuotas.reduce(
    (acc, row) => ({
      easy: acc.easy + row.easy,
      medium: acc.medium + row.medium,
      hard: acc.hard + row.hard,
    }),
    { easy: 0, medium: 0, hard: 0 }
  );

  const baseLayout = layout && typeof layout === 'object' ? layout : {};
  const newLayout = {
    ...baseLayout,
    version: baseLayout.version || MST_LAYOUT_VERSION,
    questionsPerModule: baseLayout.questionsPerModule || MST_DEFAULT_TARGET_QUESTION_COUNT,
    moduleCount: newSectors.length,
    totalQuestions: newQuestions.length,
    wordCount: Number(wordCount) || baseLayout.wordCount || 0,
    globalDifficulty,
    perModuleQuestionTargets,
    perModuleDifficultyQuotas,
    minQuestionsPerModule: MST_MIN_QUESTIONS_PER_MODULE,
    maxQuestionsPerModule: MST_MAX_QUESTIONS_PER_MODULE,
    minTotalForTripleFour: MST_MIN_TOTAL_FOR_TRIPLE_FOUR,
  };

  return { questions: newQuestions, sectors: newSectors, mstLayout: newLayout };
}
